import { MapPin, Navigation, Plane, Anchor } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const PROPERTY_COORDS = "39.99192533706946,23.60444950493526";
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  PROPERTY_COORDS
)}`;
const EMBED_SRC = `https://www.google.com/maps?q=${encodeURIComponent(
  PROPERTY_COORDS
)}&z=16&output=embed`;

// Icons cycle by index — kept in code so the design stays consistent.
const ICONS = [Anchor, Navigation, Plane, MapPin];

export default function Location() {
  const { content } = useContent();
  const nearby = content.nearby || [];

  return (
    <section
      id="location"
      data-testid="location-section"
      className="py-20 md:py-28 bg-[#FAF7F2]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="overline">Where you&apos;ll be</p>
            <h2
              data-testid="location-title"
              className="font-serif-display text-4xl md:text-5xl mt-3 text-[#232E26]"
            >
              Pefkochori,{" "}
              <em className="not-italic text-[#1A5A7B]">Halkidiki.</em>
            </h2>
          </div>
          <p className="text-[#5B6E4A] max-w-md leading-relaxed">
            On the Kassandra peninsula — quiet pine-shaded streets, golden
            beaches and warm village life, all within walking distance of
            Mirabella Anemoessa.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
          <div
            data-testid="map-embed-wrapper"
            className="md:col-span-8 relative overflow-hidden border border-[#EAE0D5] bg-[#EAE0D5] aspect-[16/10] md:aspect-[16/11]"
          >
            <iframe
              data-testid="map-iframe"
              title="Mirabella Anemoessa location — Pefkochori, Halkidiki, Greece"
              src={EMBED_SRC}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
            />
          </div>

          <aside className="md:col-span-4 bg-white border border-[#EAE0D5] p-8 flex flex-col">
            <div className="flex items-start gap-3">
              <span className="h-10 w-10 rounded-full bg-[#EAE0D5] flex items-center justify-center shrink-0">
                <MapPin size={18} className="text-[#C65D47]" />
              </span>
              <div>
                <p className="overline">Address</p>
                <p
                  data-testid="location-address"
                  className="mt-1 font-serif-display text-xl text-[#232E26] leading-tight"
                >
                  Pefkochori,
                  <br />
                  Halkidiki, Greece
                </p>
                <p className="mt-2 text-sm text-[#5B6E4A]">
                  Exact street address shared on booking confirmation.
                </p>
              </div>
            </div>

            <div className="divider-line my-7 max-w-full" />

            <ul className="space-y-5">
              {nearby.map((n, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <li
                    key={i}
                    data-testid={`nearby-${i}`}
                    className="flex items-start gap-3"
                  >
                    <span className="h-9 w-9 rounded-full bg-[#FAF7F2] border border-[#EAE0D5] flex items-center justify-center shrink-0">
                      <Icon size={15} className="text-[#5B6E4A]" />
                    </span>
                    <div>
                      <p className="text-[#232E26] text-[15px]">{n.title}</p>
                      <p className="text-[#5B6E4A] text-sm">{n.body}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            <a
              data-testid="open-in-maps"
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-terra mt-8 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm text-sm tracking-wide uppercase"
            >
              Open in Google Maps
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}
